import { ref, onMounted, onUnmounted } from "vue";

const isTouchDevice = (): boolean =>
{
	const isTouch =
		"ontouchstart" in window ||
		navigator.maxTouchPoints > 0;

	const isSmallViewport = window.innerWidth <= 767;

	return isTouch || isSmallViewport;
};

/**
 * Vérifie si l'écran est de taille tablette (>= 768px)
 */
const isTablet = (): boolean =>
{
	return window.innerWidth >= 768;
};

/**
 * Composable réactif pour détecter si on est sur tablette
 */
const useIsTablet = () =>
{
	const isTabletRef = ref(window.innerWidth >= 768);

	const updateIsTablet = () =>
	{
		isTabletRef.value = window.innerWidth >= 768;
	};

	onMounted(() =>
	{
		window.addEventListener("resize", updateIsTablet);
	});

	onUnmounted(() =>
	{
		window.removeEventListener("resize", updateIsTablet);
	});

	return isTabletRef;
};

/**
 * Composable réactif : vrai dès ≥ md (768px). Alias sémantique de useIsTablet (le breakpoint
 * md du projet = 768px). Pour les bascules "layout desktop" (ex. retirer la bottom bar mobile
 * du DOM au-delà de md).
 */
const useIsMdUp = () => useIsTablet();

/**
 * Composable réactif pour détecter si on est en lg+ (992px = breakpoint lg)
 */
const useIsLargeScreen = () =>
{
	const isLargeScreenRef = ref(window.innerWidth >= 992);

	const updateIsLargeScreen = () =>
	{
		isLargeScreenRef.value = window.innerWidth >= 992;
	};

	onMounted(() =>
	{
		window.addEventListener("resize", updateIsLargeScreen);
	});

	onUnmounted(() =>
	{
		window.removeEventListener("resize", updateIsLargeScreen);
	});

	return isLargeScreenRef;
};

/**
 * Composable réactif : largeur du viewport en px, mise à jour au resize. Pour les paliers fins
 * (lg/xl/xxl) où un simple booléen ne suffit pas (ex. nombre de semaines du calendrier).
 */
const useViewportWidth = () =>
{
	const widthRef = ref(window.innerWidth);

	const update = () =>
	{
		widthRef.value = window.innerWidth;
	};

	onMounted(() =>
	{
		window.addEventListener("resize", update);
	});

	onUnmounted(() =>
	{
		window.removeEventListener("resize", update);
	});

	return widthRef;
};

/**
 * Composable réactif : orientation PAYSAGE (largeur > hauteur), via matchMedia.
 * Combiné à `useIsTablet` côté page : landscape → 3 colonnes ; portrait → layout resserré (2 colonnes).
 */
const useIsTabletLandscape = () =>
{
	const mql = window.matchMedia("(orientation: landscape)");
	const isTabletLandscapeRef = ref(mql.matches);

	const update = () =>
	{
		isTabletLandscapeRef.value = mql.matches;
	};

	onMounted(() =>
	{
		mql.addEventListener("change", update);
		// Filet : certains WebViews ne déclenchent pas 'change' de façon fiable au pivot → resize aussi.
		window.addEventListener("resize", update);
	});

	onUnmounted(() =>
	{
		mql.removeEventListener("change", update);
		window.removeEventListener("resize", update);
	});

	return isTabletLandscapeRef;
};

/**
 * Détecte si on tourne dans une app Capacitor (ionic/coach) plutôt que sur le web (web/admin/bp).
 * Capacitor injecte `window.Capacitor` au démarrage.
 */
const isApp = (): boolean =>
{
	return typeof window !== "undefined"
		&& typeof (window as any).Capacitor !== "undefined"
		&& (window as any).Capacitor.isNativePlatform?.() === true;
};

/**
 * Web/admin : présence du cookie « hint » de session (posé par l'API à la connexion, non httpOnly).
 * Indique qu'un refresh token existe (en cookie httpOnly invisible au JS) → tenter un silent refresh
 * au boot. Absent = visiteur anonyme → aucun appel, rendu immédiat en guest (pas de flash).
 */
const hasSessionHint = (): boolean =>
{
	return typeof document !== "undefined"
		&& document.cookie.split("; ").some(c => c.startsWith("logged_in="));
};

const isDevEnv = (): boolean =>
{
	// VITE_ENV est défini dans .env et inclus au build
	return import.meta.env.VITE_ENV === "development" || import.meta.env.MODE === "development";
};

const isStagingEnv = (): boolean =>
{
	return import.meta.env.VITE_ENV === "staging";
};

const isProductionEnv = (): boolean =>
{
	return import.meta.env.VITE_ENV === "production";
};

function getOS()
{
	const ua = navigator.userAgent;

	if (/iPhone|iPad|iPod/i.test(ua))
	{
		return "ios";
	}

	if (/iPad/i.test(ua))
	{
		return "ipados";
	}

	if (/Mac/i.test(ua))
	{
		return "mac";
	}
	if (/Win/i.test(ua))
	{
		return "windows";
	}
	if (/Linux/i.test(ua))
	{
		return "linux";
	}
	if (/Android/i.test(ua))
	{
		return "android";
	}

	return "unknown";
}

async function getOsVersion()
{
	const ua = navigator.userAgent;
	const match = ua.match(/Android (\d+(?:\.\d+)?)/) || ua.match(/iPhone OS (\d+(?:[_\.]\d+)?)/);
	return match ? match[1].replace("_", ".") : "unknown";
}

function getBrowser()
{
	const ua = navigator.userAgent;

	// Détection iOS (iPhone/iPad) dans une WebView (Ionic/Capacitor/Cordova)
	if (/iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && ! /Safari/.test(ua))
	{
		return { name: "iOS WebView", version: "unknown" };
	}

	// 🔹 Safari (macOS ou iOS natif)
	if (/Version\/[\d.]+.*Safari/.test(ua) && ! /Chrome|Chromium|Edg/.test(ua))
	{
		const version = ua.match(/Version\/(\d+(\.\d+)?)/)?.[1] || "unknown";
		return { name: "Safari", version };
	}

	// 🔹 Edge Chromium
	if (/Edg\/(\d+(\.\d+)?)/.test(ua))
	{
		const version = ua.match(/Edg\/(\d+(\.\d+)?)/)?.[1] || "unknown";
		return { name: "Edge", version };
	}

	// 🔹 Samsung Internet
	if (/SamsungBrowser\/(\d+(\.\d+)?)/.test(ua))
	{
		const version = ua.match(/SamsungBrowser\/(\d+(\.\d+)?)/)?.[1] || "unknown";
		return { name: "Samsung Internet", version };
	}

	// 🔹 Firefox
	if (/Firefox\/(\d+(\.\d+)?)/.test(ua))
	{
		const version = ua.match(/Firefox\/(\d+(\.\d+)?)/)?.[1] || "unknown";
		return { name: "Firefox", version };
	}

	// 🔹 Chrome (desktop + android)
	// Important : doit être après Edge et Samsung
	if (/Chrome\/(\d+(\.\d+)?)/.test(ua) && ! /Edg|SamsungBrowser/.test(ua))
	{
		const version = ua.match(/Chrome\/(\d+(\.\d+)?)/)?.[1] || "unknown";
		return { name: "Chrome", version };
	}

	// 🔹 Chromium pur
	if (/Chromium\/(\d+(\.\d+)?)/.test(ua))
	{
		const version = ua.match(/Chromium\/(\d+(\.\d+)?)/)?.[1] || "unknown";
		return { name: "Chromium", version };
	}

	return { name: "unknown", version: "unknown" };
}

export {
	isTouchDevice,
	isTablet,
	useIsTablet,
	useIsMdUp,
	useIsLargeScreen,
	useViewportWidth,
	useIsTabletLandscape,
	isApp,
	hasSessionHint,
	isDevEnv,
	isStagingEnv,
	isProductionEnv,
	getOS,
	getOsVersion,
	getBrowser
};
